import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { 
  DashboardApiService, 
  IDashboardOverviewData, 
  IDashboardOverviewParams,
  IOverviewOverdueCustomer
} from '@mavles/dashboard';
import { AlertService } from '@mavles/shared';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';

export type OverviewTab = 'overview' | 'customer_accounts' | 'reminders';

export interface TabItem {
  id: OverviewTab;
  label: string;
  icon: string;
}

export type OverviewPreset = 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year' | 'custom';

@Component({
  selector: 'app-dashboard-overview',
  templateUrl: './dashboard-overview.component.html',
  styleUrl: './dashboard-overview.component.scss',
  standalone: false,
})
export class DashboardOverviewComponent implements OnInit {
  private dashboardService = inject(DashboardApiService);
  private alertService = inject(AlertService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  activeTab: OverviewTab = 'overview';

  tabs: TabItem[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'bi bi-grid-1x2',
    },
    {
      id: 'customer_accounts',
      label: 'Customer accounts',
      icon: 'bi bi-person-lines-fill',
    },
    {
      id: 'reminders',
      label: 'Follow-Ups & Reminders',
      icon: 'bi bi-bell-fill',
    },
  ];

  // Overview State
  overviewData: IDashboardOverviewData | null = null;
  loading = false;
  lastUpdated: Date | null = null;

  // Filter State
  selectedPreset: OverviewPreset = 'this_year';
  startDate = '';
  endDate = '';
  showCustomDates = false;

  presets: { id: OverviewPreset; label: string }[] = [
    { id: 'today', label: 'Today' },
    { id: 'this_week', label: 'This Week' },
    { id: 'this_month', label: 'This Month' },
    { id: 'this_quarter', label: 'This Quarter' },
    { id: 'this_year', label: 'This Year' },
    { id: 'custom', label: 'Custom' },
  ];

  // Daily Trend Chart Configuration
  public dailyChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 10,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 12, weight: 'bold' }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        titleFont: { size: 13, weight: 'bold' },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        displayColors: true
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } }
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Bricks Dispatched (Qty)' },
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { font: { size: 11 } }
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        title: { display: true, text: 'Orders / Invoices' },
        grid: { drawOnChartArea: false },
        ticks: { precision: 0, font: { size: 11 } }
      }
    }
  };

  public dailyChartType: ChartType = 'bar';
  public dailyChartData: ChartData<'bar' | 'line'> = {
    labels: [],
    datasets: []
  };

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['tab'] && (params['tab'] === 'overview' || params['tab'] === 'customer_accounts' || params['tab'] === 'reminders')) {
        this.activeTab = params['tab'];
      }
      if (this.activeTab === 'overview' && !this.overviewData) {
        this.loadOverview();
      }
    });
  }

  selectTab(tab: OverviewTab): void {
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge'
    });
    if (this.activeTab === 'overview' && !this.overviewData) {
      this.loadOverview();
    }
  }

  loadOverview(): void {
    this.loading = true;
    const params: IDashboardOverviewParams = {};

    if (this.selectedPreset === 'custom') {
      params.preset = 'custom';
      if (this.startDate) params.start_date = this.startDate;
      if (this.endDate) params.end_date = this.endDate;
    } else {
      params.preset = this.selectedPreset;
    }

    this.dashboardService.getOverviewDashboard(params).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success && response.data) {
          this.overviewData = response.data;
          this.lastUpdated = new Date();

          if (response.data.period) {
            if (response.data.period.start_date) {
              this.startDate = response.data.period.start_date.slice(0, 10);
            }
            if (response.data.period.end_date) {
              this.endDate = response.data.period.end_date.slice(0, 10);
            }
          }
          this.prepareDailyChart();
        } else {
          this.alertService.error(response.message || 'Failed to load overview data.');
        }
      },
      error: (err) => {
        this.loading = false;
        console.error('Overview dashboard error:', err);
        this.alertService.error(err?.error?.message || err?.message || 'Error connecting to overview API.');
      }
    });
  }

  setPreset(preset: OverviewPreset): void {
    this.selectedPreset = preset;
    this.showCustomDates = preset === 'custom';
    if (preset !== 'custom') {
      this.loadOverview();
    }
  }

  applyCustomRange(): void {
    if (!this.startDate || !this.endDate) {
      this.alertService.warn('Please select both start and end dates.');
      return;
    }
    this.selectedPreset = 'custom';
    this.loadOverview();
  }

  prepareDailyChart(): void {
    const trend = this.overviewData?.sales_volume?.daily_volume_trend || [];
    const labels = trend.map(t => {
      if (!t.date) return '';
      const parts = t.date.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}`;
      }
      return t.date;
    });

    const brickCounts = trend.map(t => Number(t.brick_count) || 0);
    const orderCounts = trend.map(t => Number(t.orders_count) || 0);

    this.dailyChartData = {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Bricks Dispatched',
          data: brickCounts,
          backgroundColor: 'rgba(59, 130, 246, 0.78)',
          hoverBackgroundColor: 'rgba(37, 99, 235, 0.95)',
          borderRadius: 4,
          borderSkipped: false,
          yAxisID: 'y'
        },
        {
          type: 'line',
          label: 'Invoices Count',
          data: orderCounts,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.15)',
          borderWidth: 2.5,
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#10b981',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 1.5,
          fill: true,
          yAxisID: 'y1'
        }
      ]
    };

    setTimeout(() => {
      this.chart?.update();
    }, 50);
  }

  getProductShare(quantity: number): number {
    const total = this.overviewData?.sales_volume?.total_bricks_sold || 0;
    if (total <= 0) return 0;
    return Math.min(100, Math.round((quantity / total) * 100));
  }

  getNetStockDelta(): number {
    const produced = this.overviewData?.production_operations?.total_bricks_produced || 0;
    const sold = this.overviewData?.sales_volume?.total_bricks_sold || 0;
    return produced - sold;
  }

  getProductionEfficiency(): number {
    const produced = this.overviewData?.production_operations?.total_bricks_produced || 0;
    const sold = this.overviewData?.sales_volume?.total_bricks_sold || 0;
    if (produced <= 0) return 0;
    return Math.min(100, Math.round((sold / produced) * 100));
  }

  getInitials(name?: string): string {
    if (!name) return 'C';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  auditCustomer(customer: IOverviewOverdueCustomer): void {
    this.selectTab('customer_accounts');
  }

  getTotalCustomers(): number {
    const summary = this.overviewData?.contacts_summary as any;
    if (!summary) return 0;
    if (summary.open_customers_count !== undefined && summary.closed_customers_count !== undefined) {
      return (Number(summary.open_customers_count) || 0) + (Number(summary.closed_customers_count) || 0);
    }
    return (Number(summary.total_active_contacts) || 0) + (Number(summary.closed_accounts_count) || 0);
  }

  getClosedCustomersCount(): number {
    const summary = this.overviewData?.contacts_summary as any;
    if (!summary) return 0;
    if (summary.closed_customers_count !== undefined) {
      return Number(summary.closed_customers_count) || 0;
    }
    return Number(summary.closed_accounts_count) || 0;
  }

  getActiveCustomersCount(): number {
    const summary = this.overviewData?.contacts_summary as any;
    if (!summary) return 0;
    if (summary.open_customers_count !== undefined) {
      return Number(summary.open_customers_count) || 0;
    }
    return Number(summary.total_active_contacts) || 0;
  }

  getAvgBricksPerOrder(): number {
    const sold = this.overviewData?.sales_volume?.total_bricks_sold || 0;
    const orders = this.overviewData?.sales_volume?.total_orders_count || 0;
    if (orders <= 0) return 0;
    return Math.round(sold / orders);
  }

  getClosurePercentage(): number {
    const total = this.getTotalCustomers();
    const closed = this.getClosedCustomersCount();
    if (total <= 0) return 0;
    return Math.min(100, Math.round((closed / total) * 100));
  }

  getClosureSeverity(): 'success' | 'warning' | 'danger' {
    const pct = this.getClosurePercentage();
    if (pct >= 80) return 'success';
    if (pct >= 50) return 'warning';
    return 'danger';
  }

  getClosureSeverityStatus(): string {
    const pct = this.getClosurePercentage();
    if (pct >= 80) return 'Target Met (>=80% Closed)';
    if (pct >= 50) return 'Moderate (<80% Closed)';
    return 'Danger (<80% Target)';
  }

  getClosureSeverityCardClass(): string {
    const severity = this.getClosureSeverity();
    if (severity === 'success') return 'metric-card-green';
    if (severity === 'warning') return 'metric-card-warning';
    return 'metric-card-danger';
  }
}

